import {
  BookingType,
  CustomerTypes,
  IBooking,
  ICustomer,
  IDiarySchedule,
  IDiaryVenue,
  IVenue
} from "shared-types/index";

import NetworkConnectionService from 'external-services//network-connection-service/index';
import UtilsService from "external-services/utils-service/index";
import {AxiosInstance} from "axios";
import { appURLS } from "appRoot/app.constants";

import CustomerNormaliseService from "external-services/customer-normalise-service";
import NetworkRequestsService from 'external-services/network-requests-service';
import AdminService from "external-services/diary-admin-service/admin.service";
import BookingLookupService from "external-services/booking-lookup-service/index";
import BookingListFilterService from "external-services/booking-list-filter-service/index";
import { cloneDeep, sortBy, capitalize } from 'lodash-es';
import CustomerValidationService from 'external-services/customer-validation-service/customerValidation.service';
import { CustomerGeneratorService } from 'external-services/customer-generator-service';

interface ICustomerSearchInput {
  q: string;
  sort?: string;
  start?: number;
  count?: number;
  field?: string;
  countryCode?: string;
}

export interface ICustomerDifference {
  key: string;
  label?:string;
  original: string | number | boolean;
  new: string | number | boolean;
}
interface ISpecificSearchInput {
  count: number;
  searchFields: { fieldName: string; value: string }[];
}

export enum RestrictResult {
  NotRestrict = 'NotRestrict',
  TryConfirm = 'TryConfirm',
  CancelPreviousSBLConfirmation = 'CancelPreviousSBLConfirmation',
}

export enum RestrictSBLError {
  networkIssue = 'networkIssue',
  networkOffline = 'networkOffline',
}

enum CustomerID {
  isUndefined = 'isUndefined',
  isNull = 'isNull',
}

enum RestrictState {
  isTrue = 'isTrue',
  isFalse = 'isFalse',
  isUndefined = 'isUndefined',
}

const NS = 'CustomerService'; // jshint ignore:line

const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

export const getApiInstanceForTests = (): AxiosInstance => api as AxiosInstance;

class CustomerSearchService {
  private getVenue: () => IDiaryVenue;

  public init(getVenue: () => IDiaryVenue): void {
    this.getVenue = getVenue;
  }

  /**
   * @param {Object} [inputs] - The search params
   * @param {string} [inputs.q] - Search query
   * @param {string} [inputs.sort] - Sort filter (-<name> for descending)
   * @param {number} [inputs.start] - Index of first item to return
   * @param {number} [inputs.count] - Number of items to return
   * @param {boolean} [isAccountLevel] - Search against the whole account
   * @returns {items: Array<Object> | Array<string>}
   */
  public async search(
    inputs: ICustomerSearchInput,
    isAccountLevel = false
  ): Promise<{
    items: ICustomer[];
    count: number;
  }> {
    const params: any = inputs || {};
    const result: {
      items: ICustomer[];
      count: number;
    } = {
      items: [],
      count: 0,
    };
    /**
     * the offline customer logic is no longer used, because isOnlineCustomersEnabled will always true
     */
    if (this.isOffline()) {
      //remove isOnlineCustomersEnabled condition here
      return result;
    }

    try {
      const response = await api.get(
        this.getSearchCustomerEndpointsURL(isAccountLevel), { params: params }
      );
      result.items = response.data.map((item: ICustomer) =>
        CustomerNormaliseService.normalizeModelToView(item, true)
      );
      result.count = UtilsService.getResponseItemsCount(response);
      return result;
    } catch (e) {
      return result;
    }
  }
  public async specificSearch(
    search: ISpecificSearchInput,
    isAccountLevel = false
  ): Promise<{
    items: ICustomer[];
    count: number;
  }> {
    const result: {
      items: ICustomer[];
      count: number;
    } = {
      items: [],
      count: 0,
    };
    if (this.isOffline()) {
      return result;
    }
    try {
      const response = await api.post(
        this.getSearchCustomerEndpointsURL(isAccountLevel),
        search
      );
      return {
        items: response.data.map((item: ICustomer) =>
          CustomerNormaliseService.normalizeModelToView(item, true)
        ),
        count: UtilsService.getResponseItemsCount(response),
      };
    } catch (e) {
      console.log('specificSearch error', e);
      return result;
    }
  }

  public getSearchCustomerEndpointsURL(isAccountLevel: any): string {
    return (
      appURLS.API +
      (isAccountLevel
        ? `/restaurants/search-customers/by-account/${this.getVenue().accountId}`
        : `/restaurants/search-customers/by-venue/${this.getVenue().id}`)
    );
  }

  public async checkExistingCustomer(customer: ICustomer): Promise<{
    status: boolean;
    error?: string;
    data?: any;
  }> {
    const result: {
      items: ICustomer[];
      count: number;
    } = {
      items: [],
      count: 0,
    };
    if (this.isOffline()) {
      return {
        status: false,
        error: RestrictSBLError.networkOffline,
      };
    }
    try {
      const response = await api.post(
        this.getCheckExistingCustomerEndpointURL(),
        customer
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (e) {
      console.log('checkExistingCustomer error', e);
      return {
        status: false,
        error: RestrictSBLError.networkIssue,
      };
    }
  }

  /**
   * This func doesn't change origin customer info, so after call this func, need to change customer's first and last name manually.
   */
  public async detectSBLByCustomer(
    customer: ICustomer,
    bookingType: BookingType,
    isCancelPreviousSB: boolean,
    schedule: IDiarySchedule
  ): Promise<{
    result: RestrictResult;
    customerSBL: IBooking[];
    customer: ICustomer;
  }> {
    const tempCustomer = cloneDeep(customer);

    let restrictState: RestrictState = RestrictState.isFalse;
    let customerSBL: IBooking[] = [];
    let restrictResult = RestrictResult.NotRestrict;
    if (bookingType === BookingType.StandbyList) {
      let checkCustomerId: string;
      if (tempCustomer._id) {
        checkCustomerId = tempCustomer._id;
      } else {
        /**
         * Detect last name from full name(first name) by space when inline customer
         */
        if (tempCustomer.customerType === CustomerTypes.inline) {
          const splitName = tempCustomer.firstName.split(' ');
          if (splitName && splitName.length > 1) {
            tempCustomer.lastName = splitName[splitName.length - 1];
            tempCustomer.firstName = splitName[0];
          }
        }
        const customerCheck = await this.checkExistingCustomer(tempCustomer);
        if (customerCheck?.status && customerCheck?.data) {
          checkCustomerId = customerCheck?.data._id;
        } else if (customerCheck?.status && !customerCheck?.data) {
          checkCustomerId = CustomerID.isNull;
        } else if (
          !customerCheck?.status &&
          customerCheck.error === RestrictSBLError.networkOffline
        ) {
          /**
           * Jim asked to follow standard logic
           * https://nowbookit.atlassian.net/browse/NBI-4388?focusedCommentId=74772
           */
          checkCustomerId = CustomerID.isNull;
        } else if (
          !customerCheck?.status &&
          customerCheck.error === RestrictSBLError.networkIssue
        ) {
          checkCustomerId = CustomerID.isUndefined;
        }
      }

      if (checkCustomerId === CustomerID.isUndefined) {
        restrictState = RestrictState.isUndefined;
      } else if (checkCustomerId === CustomerID.isNull) {
        restrictState = RestrictState.isFalse;
      } else {
        customerSBL = await this.getCustomerSBL(checkCustomerId, schedule);
        restrictState = customerSBL.length > 0 ? RestrictState.isTrue : RestrictState.isFalse;
      }
    }

    if (restrictState === RestrictState.isTrue && isCancelPreviousSB === false) {
      restrictResult = RestrictResult.CancelPreviousSBLConfirmation;
    } else if (restrictState === RestrictState.isUndefined) {
      /**
       * Need to display error msg `Can't save SBL because network issue`
       */
      restrictResult = RestrictResult.TryConfirm;
    }

    return {
      result: restrictResult,
      customerSBL: customerSBL,
      customer: tempCustomer,
    };
  }

  public async searchCustomer(customer: ICustomer, venue: IDiaryVenue): Promise<ICustomer[]> {
    const customerSearchArray = CustomerValidationService.getCustomerValue(customer);
    // no need to search if customerSearchArray is empty
    if (customerSearchArray.length === 0) {
      return [];
    }
    const searchOptions: any = {
      count: 15,
      searchFields: customerSearchArray,
    };
    // for new customer form search, we need to pass venueId to get permanent tags
    // we also need pass page and amount when we do pagination.
    searchOptions.options = {
      venueId: venue.id,
    };
    try {
      const result = await this.specificSearch(searchOptions, true)
      return result.items.map(customer => {
        customer.label = CustomerGeneratorService.getDisplayName(customer, true);
        return customer;
      });
    } catch (e) {
      return [];
    }
  }

  public checkDifference(originalCustomer: ICustomer, newCustomer: ICustomer): ICustomerDifference[] {
    const includeCondition = ['birthdayDate', 'birthdayMonth', 'birthdayYear', 'company',
      'email', 'firstName', 'lastName', 'phone', 'postcode', 'streetAddress', 'suburb'];
    const diffInfoList: ICustomerDifference[] = [];
    Object.keys(originalCustomer).forEach((key) => {
      if (originalCustomer[key as keyof ICustomer] !== newCustomer[key as keyof ICustomer] && (includeCondition.includes(key))) {
        if (key === 'phone') {
          if (originalCustomer[key]?.replace(/ /g, '') !== newCustomer[key]?.replace(/ /g, '')) {
            diffInfoList.push({
              key: capitalize(key),
              original: originalCustomer['phone']?.replace(/ /g, '') ?? '',
              new: newCustomer['phone']?.replace(/ /g, '') ?? ''
            });
          }
        } else {
          diffInfoList.push({
            key: capitalize(key),
            original: originalCustomer[key as keyof ICustomer] as string|number|boolean ?? '',
            new: newCustomer[key as keyof ICustomer] as string|number|boolean ?? '',
          });
        }
      }
    });

    diffInfoList.forEach((diffInfo:ICustomerDifference) => {
      switch(diffInfo.key) {
        case 'Birthdaydate':
          diffInfo.label = 'Birth Date';
          break;
        case 'Birthdaymonth':
          diffInfo.label = 'Birth Month';
          break;
        case 'Birthdayyear':
          diffInfo.label = 'Birth Year';
          break;
        case 'Firstname':
          diffInfo.label = 'First Name';
          break;
        case 'Lastname':
          diffInfo.label = 'Last Name';
          break;
        case 'Streetaddress':
          diffInfo.label = 'Street Address';
          break;
        default:
          diffInfo.label = diffInfo.key;
      }

    })
    return diffInfoList;
  }
  // ############################################ Private methods ##################################

  private isOffline(): boolean {
    return !NetworkConnectionService.isOnline();
  }

  private getCheckExistingCustomerEndpointURL(): string {
    return (
      appURLS.API +
      '/restaurants/venues/' +
      AdminService.getVenue().id +
      '/customers/check-existing'
    );
  }

  private async getCustomerSBL(customerId: string, schedule: IDiarySchedule): Promise<IBooking[]> {
    if (customerId) {
      const results = await BookingLookupService.getBySchedule(schedule);
      if (results?.schedule._id === schedule._id) {
        const bookings = results.bookings;
        // Filter the bookings
        const _bookings = bookings.filter(booking => booking.customerId === customerId);
        const bookingsList = await BookingListFilterService.getCategorisedBookingList(_bookings, true); // @todo Need to check 2nd param, vm.isStandbyListEnabled = AdminService.getVenue().standByList;
        return (bookingsList && bookingsList.standByListCount) ? bookingsList.standByListCount : [];
      }
    }

    return [];
  }

  public modifyCustomerObjectForSearch(searchCustomerObj: ICustomer, customer: ICustomer): ICustomer {
    const isValidWalkinCustomer =  Boolean(
      (searchCustomerObj?.firstName?.length || searchCustomerObj?.lastName?.length) &&
        !searchCustomerObj?.email?.length &&
        !searchCustomerObj?.phone?.length
    );
    if (isValidWalkinCustomer) {
      return {
        ...customer,
        ...searchCustomerObj
      }
    } else
    // If has email or phone value => reset customer values
    {
      const clonedCustomer = cloneDeep(customer)
      const removedProps = ['firstName', 'lastName', 'email', 'phone']
      removedProps.forEach(prop => {
        delete clonedCustomer[prop as keyof ICustomer]
      })
      return clonedCustomer
    }
  }
}

const instance = new CustomerSearchService();
export default instance as CustomerSearchService;
